import { fetchConfig } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Shipping Protection in the cart drawer (default ON, shopper can switch it off).
 * The protection is a normal product (tag `no-discount`) so it ends up on the order,
 * but it is hidden as a cart line and shown as a toggle instead.
 *
 * - Toggle on/off: adds/removes the product.
 * - Default on: when the drawer has real products and no protection, and the shopper has not
 *   switched it off earlier in this browser session, it is added automatically.
 * - If the last real product is removed, the protection is removed too (never alone in the cart).
 */

const TOGGLE = '[data-shipping-protection-toggle]';
const ORPHAN = '[data-shipping-protection-orphan]';
const AUTO = '[data-shipping-protection-auto]';
const SECTION_ID = 'cart-drawer-section';
const DECLINED_KEY = 'shipping-protection-declined';

let busy = false;
let cleanupFailed = false;
let autoFailed = false;

function isDeclined() {
  try {
    return sessionStorage.getItem(DECLINED_KEY) === '1';
  } catch (_) {
    return false;
  }
}

/** @param {boolean} declined */
function setDeclined(declined) {
  try {
    if (declined) sessionStorage.setItem(DECLINED_KEY, '1');
    else sessionStorage.removeItem(DECLINED_KEY);
  } catch (_) {
    /* storage unavailable: nothing to remember */
  }
}

/**
 * @param {number} variantId
 * @param {boolean} add
 * @param {HTMLElement} [source]
 */
async function updateProtection(variantId, add, source) {
  const root = window.Shopify?.routes?.root ?? '/';
  // cart/change.js wants the id as a string, cart/add.js wants a number.
  const body = {
    sections: SECTION_ID,
    sections_url: window.location.pathname,
    ...(add ? { items: [{ id: variantId, quantity: 1 }] } : { id: String(variantId), quantity: 0 }),
  };

  const response = await fetch(`${root}cart/${add ? 'add' : 'change'}.js`, fetchConfig('json', { body: JSON.stringify(body) }));
  const data = await response.json();
  if (!response.ok || data.errors || data.status) {
    throw new Error(data.description || data.message || 'Cart update failed');
  }

  const cart = add
    ? await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json())
    : data;

  // Item count without the protection line, so the cart badge matches what the customer sees.
  const itemCount = cart.items
    .filter((item) => item.variant_id !== variantId)
    .reduce((total, item) => total + item.quantity, 0);

  try {
    const deferred = CartLinesUpdateEvent.createPromise();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse({ ...cart, item_count: itemCount }),
      detail: {
        sections: data.sections,
        items: cart.items,
        itemCount,
        source: 'cart-shipping-protection',
        didError: false,
      },
    });
    // Not action 'add': the cart drawer opens itself on 'add' events, and adding the protection
    // must never pop the drawer open on its own.
    (source ?? document.body).dispatchEvent(
      new CartLinesUpdateEvent({
        action: add ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: String(variantId), quantity: add ? 1 : 0 }],
        promise: deferred.promise,
      })
    );
  } catch (error) {
    console.warn('[shipping-protection] event failed, re-rendering drawer', error);
    await sectionRenderer.renderSection(SECTION_ID, { cache: false, mode: 'hydration' });
  }
}

document.addEventListener('change', async (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.matches(TOGGLE) || busy) return;

  const wanted = input.checked;
  busy = true;
  input.disabled = true;

  try {
    await updateProtection(Number(input.dataset.variantId), wanted, input);
    // Remember the shopper's choice so the default-on logic does not re-add it.
    setDeclined(!wanted);
  } catch (error) {
    console.error('[shipping-protection]', error);
    input.checked = !wanted;
  } finally {
    busy = false;
    input.disabled = false;
  }
});

function syncProtection() {
  if (busy) return;

  const orphan = document.querySelector(ORPHAN);
  if (orphan instanceof HTMLElement && !cleanupFailed) {
    busy = true;
    updateProtection(Number(orphan.dataset.variantId), false)
      .catch((error) => {
        cleanupFailed = true;
        console.error('[shipping-protection] cleanup failed', error);
      })
      .finally(() => {
        busy = false;
      });
    return;
  }

  const auto = document.querySelector(AUTO);
  if (auto instanceof HTMLElement && !autoFailed && !isDeclined()) {
    busy = true;
    updateProtection(Number(auto.dataset.variantId), true)
      .catch((error) => {
        autoFailed = true;
        console.error('[shipping-protection] auto add failed', error);
      })
      .finally(() => {
        busy = false;
      });
  }
}

new MutationObserver(syncProtection).observe(document.body, { childList: true, subtree: true });
syncProtection();
